import { computed, isRef, ref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import i18n from '@/plugins/translationsPlugin'
import type { LanguageSettingsState } from '@/types/LanguageSettingsState'
import { useDeviceManagementStore, type DMLanguage } from '@nsftx/seven-client-retail-vue-dm'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'

const LS_KEY = 'sbo_settings_state'

export const useSettingsStore = defineStore('settingsStore', () => {
  const { t } = i18n.global

  const { showNotification } = useNotificationsStore()

  const { appData } = storeToRefs(useDeviceManagementStore())

  const availableLanguages = computed(
    () => appData.value?.appConfig?.data?.supportedLanguages ?? []
  )
  const languageSettings = ref<LanguageSettingsState>({
    interfaceLanguage: ''
  })

  const saveSettingsToStorage = () => {
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        languages: languageSettings.value
      })
    )

    showNotification({ variant: 'success', text: [t('settings_save_success')] })
    changeLanguageLocale(languageSettings.value.interfaceLanguage)
  }

  const getStoredSettings = () => {
    const storedSettings = JSON.parse(localStorage.getItem(LS_KEY) || '{}')
    languageSettings.value = {
      interfaceLanguage: storedSettings.languages?.interfaceLanguage ?? ''
    }
  }

  const resetSettingsForm = () => {
    getStoredSettings()
  }

  const isInterfaceLanguageSupported = (languageCode: string) => {
    return availableLanguages.value.some((lang) => lang.language_code_2 === languageCode)
  }

  // TODO: When intergrated with frazier, fetch locale json file from API and apply it
  const changeLanguageLocale = (languageCode: string) => {
    if (!isInterfaceLanguageSupported(languageCode)) {
      return
    }

    if (isRef(i18n.global.locale)) {
      i18n.global.locale.value = languageCode
    }
  }

  const loadLanguages = () => {
    const defaultLang = appData.value?.appConfig?.data?.defaultLanguage
    if (defaultLang) {
      changeLanguageLocale(defaultLang.language_code_2)
    }
  }

  const resolveBootstrapLang = (defaultLanguage: DMLanguage): string => {
    return languageSettings.value.interfaceLanguage || defaultLanguage.language_code_2
  }

  return {
    languageSettings,
    availableLanguages,
    saveSettingsToStorage,
    getStoredSettings,
    loadLanguages,
    resolveBootstrapLang,
    resetSettingsForm
  }
})
